import { CheckCircle2, Loader2, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface PreviewData {
  categories: { new: number; updated: number };
  metrics: { total: number };
  profiles: { total: number };
  weights: { total: number };
}

export function MatrixUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    setApplied(false);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch("/api/admin/matrix/upload", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(data.error || "Upload failed");
        return;
      }

      setPreview(data.preview);
      setUploadToken(data.uploadToken);
      toast.success("Matrix parsed successfully");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleApply() {
    if (!uploadToken) return;
    setApplying(true);

    try {
      const res = await fetch("/api/admin/matrix/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadToken }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(data.error || "Apply failed");
        return;
      }

      setApplied(true);
      toast.success(
        `Applied: ${data.applied.categories} categories, ${data.applied.metrics} metrics, ${data.applied.profiles} profiles, ${data.applied.weights} weights`,
      );
    } catch {
      toast.error("Apply failed");
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Master Matrix</CardTitle>
          <CardDescription>
            Upload the .xlsx spreadsheet containing the Master Matrix Executable and Weights sheets.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setPreview(null);
                setApplied(false);
              }}
              className="flex-1"
            />
            <Button onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Parse
            </Button>
          </div>

          {preview && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <h4 className="font-medium mb-3">Preview</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Categories</p>
                    <p className="font-medium">
                      {preview.categories.new} new, {preview.categories.updated} existing
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Metrics</p>
                    <p className="font-medium">{preview.metrics.total}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Profiles</p>
                    <p className="font-medium">{preview.profiles.total}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Weights</p>
                    <p className="font-medium">{preview.weights.total}</p>
                  </div>
                </div>

                <div className="mt-4 flex gap-3">
                  <Button onClick={handleApply} disabled={applying || applied}>
                    {applying ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : applied ? (
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                    ) : null}
                    {applied ? "Applied" : "Apply Changes"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
